import { type ApplicationConfig, inject, provideAppInitializer } from "@angular/core";
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig,
} from "@angular/router";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import { provideAnimations } from "@angular/platform-browser/animations";
import { MSAL_INSTANCE, MsalService } from "@azure/msal-angular";

import { routes } from "./app.routes";
import { authInterceptor } from "./core/interceptors/auth.interceptor";
import { errorInterceptor } from "./core/interceptors/error.interceptor";
import { loadingInterceptor } from "./core/interceptors/loading.interceptor";
import { MSALInstanceFactory } from "./core/auth/msal.config";
import { AUTH_PROVIDER } from "./core/auth/auth-provider.token";
import { AUTH_CONTEXT } from "./core/auth/auth-context.token";
import { SpAuthService } from "./core/auth/sp-auth.service";
import { Auth0AuthService } from "./core/auth/auth0-auth.service";
import { resolveAuthContext } from "./core/auth/resolve-auth-context";
import { readAndStripLoginHint } from "./core/auth/sso-bootstrap";
import { environment } from "../environments/environment";

import type { IAuthProvider } from "./core/auth/interfaces";

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: "always" })
    ),
    provideHttpClient(
      withInterceptors([authInterceptor, errorInterceptor, loadingInterceptor])
    ),
    provideAnimations(),
    // ── MSAL ──
    { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
    MsalService,
    // Resolve the auth context (IdP + Auth0 org) once from this host.
    {
      provide: AUTH_CONTEXT,
      useFactory: () =>
        resolveAuthContext(window.location.hostname, {
          baseHost: environment.baseHost,
          search: window.location.search,
        }),
    },
    // Active IdP implementation, selected by hostname: apex → AAD (SpAuthService);
    // <slug>.<baseHost> → Auth0 (Auth0AuthService) with organization = slug.
    {
      provide: AUTH_PROVIDER,
      useFactory: (): IAuthProvider => {
        const ctx = inject(AUTH_CONTEXT);
        return ctx.kind === "auth0" ? inject(Auth0AuthService) : inject(SpAuthService);
      },
    },
    // ── SSO bootstrap: silent acquisition (login_hint) → redirect fallback ──
    provideAppInitializer(() => {
      const provider = inject(AUTH_PROVIDER);
      const loginHint = readAndStripLoginHint();
      return provider.initialize(loginHint);
    }),
  ],
};
